import React, { useState, useRef, useEffect } from "react";
import { FaTimes, FaDownload, FaTrash, FaLayerGroup, FaSave, FaFont, FaImage, FaTable, FaLock, FaShapes, FaFileExport, FaFileImport } from "react-icons/fa";
import { Stage, Layer, Text, Rect, Image as KonvaImage, Line, Group, Circle, Arrow, Transformer } from "react-konva";
import jsPDF from "jspdf";
import Konva from "konva";
import * as Y from "yjs";
import { UndoManager } from "yjs";
import { WebsocketProvider } from "y-websocket";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

const API_URL = "/api";

// Custom hook to load images
const useImage = (src) => {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!src) return;
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.src = src;
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
  }, [src]);
  return image;
};

// Custom component for rendering Konva images
const KonvaImageComponent = ({ src, x, y, width, height, rotation, opacity, onClick, onDragEnd }) => {
  const image = useImage(src);
  return (
    <KonvaImage
      x={x}
      y={y}
      width={width}
      height={height}
      image={image}
      rotation={rotation}
      opacity={opacity}
      draggable
      onClick={onClick}
      onDragEnd={onDragEnd}
    />
  );
};

// Initialize Yjs for real-time collaboration
const yDoc = new Y.Doc();
const wsProvider = new WebsocketProvider("wss://y-websocket-server.example.com", "pdf-designer", yDoc);
const yElements = yDoc.getArray("elements");
const yAudit = yDoc.getArray("audit");
const undoManager = new UndoManager(yElements);

const pageSizes = {
  "A4 Portrait": { width: 595, height: 842 },
  "A4 Landscape": { width: 842, height: 595 },
  "A3 Portrait": { width: 842, height: 1191 },
  "A3 Landscape": { width: 1191, height: 842 },
  "Letter Portrait": { width: 612, height: 792 },
  "Letter Landscape": { width: 792, height: 612 },
  "Legal Portrait": { width: 612, height: 1008 },
  "Legal Landscape": { width: 1008, height: 612 },
};

const PDFDesigner = ({ isOpen, onClose, entity, sampleData, availableFields }) => {
  const [elements, setElements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [layers, setLayers] = useState([{ id: "layer1", name: "Base Layer", visible: true, locked: false }]);
  const [activeLayer, setActiveLayer] = useState("layer1");
  const [components, setComponents] = useState([]);
  const [gridVisible, setGridVisible] = useState(true);
  const [auditTrail, setAuditTrail] = useState([]);
  const [pageWidth, setPageWidth] = useState(595);
  const [pageHeight, setPageHeight] = useState(842);
  const [selecting, setSelecting] = useState(false);
  const [selectStart, setSelectStart] = useState(null);
  const [selectRect, setSelectRect] = useState(null);
  const [savedDesigns, setSavedDesigns] = useState([]); // New state for saved designs
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const containerRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);

  // Fetch saved designs on mount
  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/designs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedDesigns(response.data);
      } catch (error) {
        console.error("Error fetching designs:", error);
      }
    };
    fetchDesigns();
  }, []);

  // Fetch existing design for the entity
  useEffect(() => {
    const fetchDesign = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/designs/${entity}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { elements, layers, pageWidth, pageHeight } = response.data;
        setElements(elements);
        setLayers(layers);
        setPageWidth(pageWidth);
        setPageHeight(pageHeight);
        yElements.delete(0, yElements.length);
        yElements.push(elements);
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error("Error fetching design:", error);
        }
      }
    };
    fetchDesign();
  }, [entity]);

  // Sync Yjs with local state
  useEffect(() => {
    const elementsObserver = () => {
      setElements(yElements.toArray());
    };
    const auditObserver = () => {
      setAuditTrail(yAudit.toArray());
    };
    yElements.observe(elementsObserver);
    yAudit.observe(auditObserver);
    return () => {
      yElements.unobserve(elementsObserver);
      yAudit.unobserve(auditObserver);
      wsProvider.disconnect();
    };
  }, []);

  // Clean up selectedIds when elements change
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => elements.some((el) => el.id === id)));
  }, [elements]);

  // Set stage size to container and center
  useEffect(() => {
    const resize = () => {
      if (containerRef.current && stageRef.current) {
        stageRef.current.width(containerRef.current.clientWidth);
        stageRef.current.height(containerRef.current.clientHeight);
        centerStage();
      }
    };
    window.addEventListener("resize", resize);
    resize();
    return () => window.removeEventListener("resize", resize);
  }, [pageWidth, pageHeight]);

  const centerStage = () => {
    if (stageRef.current && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const scale = Math.min(containerWidth / pageWidth, containerHeight / pageHeight, 1);
      stageRef.current.scale({ x: scale, y: scale });
      stageRef.current.position({
        x: (containerWidth - pageWidth * scale) / 2,
        y: (containerHeight - pageHeight * scale) / 2,
      });
      stageRef.current.batchDraw();
    }
  };

  useEffect(() => {
  if (!transformerRef.current || !stageRef.current) return;

  if (selectedIds.length === 0) {
    transformerRef.current.nodes([]);
    transformerRef.current.getLayer()?.batchDraw();
    return;
  }

  const selectedNodes = selectedIds
    .map((id) => stageRef.current.findOne(`#${id}`))
    .filter(Boolean);

  transformerRef.current.nodes(selectedNodes);

  const selectedEl =
    selectedIds.length === 1
      ? elements.find((el) => el.id === selectedIds[0])
      : null;

  const keepRatio =
    selectedIds.length === 1 &&
    (selectedEl?.type === "image" ||
      (selectedEl?.type === "shape" && selectedEl?.shapeType === "circle"));

  transformerRef.current.keepRatio(keepRatio);
  transformerRef.current.enabledAnchors(
    keepRatio
      ? ["top-left", "top-right", "bottom-left", "bottom-right"]
      : undefined
  );

  transformerRef.current.getLayer()?.batchDraw();
}, [selectedIds, elements]); // Keyboard handlers for undo/redo and delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Delete" && selectedIds.length > 0) {
        selectedIds.forEach((id) => removeElement(id));
        setSelectedIds([]);
      } else if (e.ctrlKey && e.key === "z") {
        if (undoManager.undoStack.length > 0) {
          undoManager.undo();
        }
      } else if (e.ctrlKey && e.key === "y") {
        if (undoManager.redoStack.length > 0) {
          undoManager.redo();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIds]);

  // Calculate table dimensions based on content
  const calculateTableDimensions = (dataFields, items, headerFontSize, bodyFontSize, cellPadding, headerHeight, rowHeight) => {
    const minColumnWidth = 80;
    const columnWidths = (dataFields || []).map((field) => {
      const maxTextLength = Math.max(
        field.length,
        ...(items || []).map((item) => `${item[field] || "N/A"}`.length)
      );
      return Math.max(minColumnWidth, maxTextLength * (bodyFontSize / 2) + cellPadding * 2);
    });
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const totalHeight = headerHeight + (items?.length || 0) * rowHeight;
    return { width: totalWidth, height: totalHeight, columnWidths };
  };

  // Add new element
  const addElement = (type, field = null, additionalProps = {}) => {
    let newEl;
    if (type === "table") {
      const headerFontSize = 14;
      const bodyFontSize = 12;
      const cellPadding = 10;
      const headerHeight = headerFontSize * 2;
      const rowHeight = bodyFontSize * 2;
      const { width, height, columnWidths } = calculateTableDimensions(
        availableFields.Items || [],
        sampleData.Items || [],
        headerFontSize,
        bodyFontSize,
        cellPadding,
        headerHeight,
        rowHeight
      );
      newEl = {
        id: Date.now(),
        type,
        layer: activeLayer,
        x: 50,
        y: 50,
        width,
        height,
        columnWidths,
        headerHeight,
        rowHeight,
        fontSize: 16,
        fontFamily: "Arial",
        fill: "#000000",
        headerFontSize,
        headerFill: "#2b6777",
        headerBgColor: "#e6f3f7",
        bodyFontSize,
        bodyFill: "#000000",
        cellPadding,
        bgColor: "#ffffff",
        borderColor: "#2b6777",
        borderWidth: 2,
        gridBorderColor: "#2b6777",
        gridBorderWidth: 1,
        headerTextAlign: "center",
        bodyTextAlign: "left",
        dataFields: availableFields.Items || [],
        rotation: 0,
        opacity: 1,
        shadowColor: "black",
        shadowBlur: 0,
        ...additionalProps,
      };
    } else {
      newEl = {
        id: Date.now(),
        type,
        layer: activeLayer,
        x: 50,
        y: 50,
        width: type === "shape" ? 100 : type === "image" ? 200 : 200,
        height: type === "shape" ? 100 : type === "image" ? 150 : 50,
        text: type === "text" ? (field ? `{data.${field}}` : "Text") : "",
        fontSize: 16,
        fontFamily: "Arial",
        fill: "#000000",
        textAlign: "left",
        fontStyle: "normal",
        bgColor: type === "shape" ? "transparent" : "transparent",
        borderColor: type === "shape" ? "#2b6777" : "#000000",
        borderWidth: type === "text" ? 0 : 2,
        imageUrl: type === "image" ? "" : null,
        shapeType: type === "shape" ? "rectangle" : null,
        points: type === "shape" && (additionalProps.shapeType === "line" || additionalProps.shapeType === "arrow") ? [0, 0, 100, 0] : null,
        rotation: 0,
        opacity: 1,
        shadowColor: "black",
        shadowBlur: 0,
        ...additionalProps,
      };
    }
    yElements.push([newEl]);
    yAudit.push([{ id: newEl.id, action: "add", timestamp: new Date().toISOString(), user: "User" }]);
    setSelectedIds([newEl.id]);
  };

  // Add shape with cycling types
  const addShape = () => {
    const shapeTypes = ["rectangle", "circle", "arrow", "triangle", "line"];
    const lastShape = elements.filter((el) => el.type === "shape").slice(-1)[0];
    const nextShapeType = lastShape ? shapeTypes[(shapeTypes.indexOf(lastShape.shapeType) + 1) % shapeTypes.length] : "rectangle";
    addElement("shape", null, { shapeType: nextShapeType });
  };

  // Update element with audit trail
  const updateElement = (id, props) => {
    const index = elements.findIndex((el) => el.id === id);
    if (index !== -1) {
      const updatedEl = { ...elements[index], ...props };
      yElements.delete(index, 1);
      yElements.insert(index, [updatedEl]);
      yAudit.push([{ id, action: "update", props, timestamp: new Date().toISOString(), user: "User" }]);
    }
  };

  // Remove element
  const removeElement = (id) => {
    const index = elements.findIndex((el) => el.id === id);
    if (index !== -1) {
      yElements.delete(index, 1);
      yAudit.push([{ id, action: "remove", timestamp: new Date().toISOString(), user: "User" }]);
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
    }
  };

  // Save component as reusable
  const saveComponent = () => {
    if (selectedIds.length === 1) {
      const el = elements.find((e) => e.id === selectedIds[0]);
      setComponents((prev) => [...prev, { ...el, componentId: uuidv4() }]);
    }
  };

  // Add reusable component
  const addComponent = (component) => {
    const newEl = { ...component, id: Date.now(), layer: activeLayer };
    yElements.push([newEl]);
    yAudit.push([{ id: newEl.id, action: "add_component", timestamp: new Date().toISOString(), user: "User" }]);
    setSelectedIds([newEl.id]);
  };

  // Add new layer
  const addLayer = () => {
    const newLayer = { id: `layer${layers.length + 1}`, name: `Layer ${layers.length + 1}`, visible: true, locked: false };
    setLayers((prev) => [...prev, newLayer]);
    setActiveLayer(newLayer.id);
  };

  // Toggle layer visibility or lock
  const toggleLayer = (layerId, property) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, [property]: !layer[property] } : layer
      )
    );
  };

  // Save design and PDF string to database
  const saveDesign = async () => {
    setIsSaving(true);
    try {
      const oldScale = stageRef.current.scaleX();
      const oldPos = stageRef.current.position();
      stageRef.current.scale({ x: 1, y: 1 });
      stageRef.current.position({ x: 0, y: 0 });
      stageRef.current.batchDraw();

      const dataURL = stageRef.current.toDataURL({
        x: 0,
        y: 0,
        width: pageWidth,
        height: pageHeight,
        pixelRatio: 4,
      });

      const pdf = new jsPDF({
        orientation: pageWidth > pageHeight ? "landscape" : "portrait",
        unit: "pt",
        format: [pageWidth, pageHeight],
      });
      pdf.addImage(dataURL, "PNG", 0, 0, pageWidth, pageHeight);
      const pdfString = pdf.output("datauristring").split(",")[1]; // Get Base64 string

      stageRef.current.scale({ x: oldScale, y: oldScale });
      stageRef.current.position(oldPos);
      stageRef.current.batchDraw();

      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/designs`,
        {
          entity,
          elements,
          layers,
          pageWidth,
          pageHeight,
          pdfString,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Design and PDF saved successfully");
    } catch (error) {
      console.error("Error saving design:", error);
      alert("Error saving design");
    } finally {
      setIsSaving(false); // Stop loading regardless of success or failure
    }
  };

  // Export design as JSON
  const exportDesign = () => {
    const design = {
      entity,
      elements,
      layers,
      pageWidth,
      pageHeight,
    };
    const blob = new Blob([JSON.stringify(design, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${entity}_design.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import design from JSON file
  const importDesign = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const design = JSON.parse(e.target.result);
          setElements(design.elements);
          setLayers(design.layers);
          setPageWidth(design.pageWidth);
          setPageHeight(design.pageHeight);
          yElements.delete(0, yElements.length);
          yElements.push(design.elements);
          alert("Design imported successfully");
        } catch (error) {
          console.error("Error importing design:", error);
          alert("Error importing design");
        }
      };
      reader.readAsText(file);
    }
  };

  // Smart grid lines with snap-to-grid
  const renderGrid = () => {
    const gridSize = 20;
    const lines = [];
    for (let i = 0; i <= pageWidth; i += gridSize) {
      lines.push(
        <Line key={`v${i}`} points={[i, 0, i, pageHeight]} stroke="#ddd" strokeWidth={0.5} />
      );
    }
    for (let i = 0; i <= pageHeight; i += gridSize) {
      lines.push(
        <Line key={`h${i}`} points={[0, i, pageWidth, i]} stroke="#ddd" strokeWidth={0.5} />
      );
    }
    return lines;
  };

  // Export PDF
  const exportPDF = () => {
    const oldScale = stageRef.current.scaleX();
    const oldPos = stageRef.current.position();
    stageRef.current.scale({ x: 1, y: 1 });
    stageRef.current.position({ x: 0, y: 0 });
    stageRef.current.batchDraw();

    const dataURL = stageRef.current.toDataURL({
      x: 0,
      y: 0,
      width: pageWidth,
      height: pageHeight,
      pixelRatio: 4,
    });

    stageRef.current.scale({ x: oldScale, y: oldScale });
    stageRef.current.position(oldPos);
    stageRef.current.batchDraw();

    const pdf = new jsPDF({
      orientation: pageWidth > pageHeight ? "landscape" : "portrait",
      unit: "pt",
      format: [pageWidth, pageHeight],
    });
    pdf.addImage(dataURL, "PNG", 0, 0, pageWidth, pageHeight);
    pdf.save(`${entity}.pdf`);
  };

  // Handle drag end with snap
  const handleDragEnd = (e, id) => {
    updateElement(id, {
      x: Math.round(e.target.x() / 20) * 20,
      y: Math.round(e.target.y() / 20) * 20,
    });
  };

  // Handle transform end
  const handleTransformEnd = (e, id, type) => {
    const node = e.target;
    let updates = {
      x: node.x(),
      y: node.y(),
      rotation: node.rotation(),
    };
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const el = elements.find((el) => el.id === id);
    updates.width = Math.max(5, el.width * scaleX);
    updates.height = Math.max(5, el.height * scaleY);
    if (type === "table") {
      updates.columnWidths = el.columnWidths.map((w) => w * scaleX);
      updates.cellPadding = el.cellPadding * scaleX;
      updates.headerFontSize = el.headerFontSize * Math.min(scaleX, scaleY);
      updates.bodyFontSize = el.bodyFontSize * Math.min(scaleX, scaleY);
      updates.headerHeight = el.headerHeight * scaleY;
      updates.rowHeight = el.rowHeight * scaleY;
      updates.width = updates.columnWidths.reduce((sum, w) => sum + w, 0);
      updates.height = updates.headerHeight + (sampleData.Items?.length || 0) * updates.rowHeight;
    } else if (type === "text") {
      // Do not scale font size
    } else if (type === "circle") {
      const radius = updates.width / 2;
      updates.x += (el.width - updates.width) / 2;
      updates.y += (el.height - updates.height) / 2;
    } else if (type === "arrow" || type === "line") {
      const newPoints = el.points.map((p, i) => p * (i % 2 === 0 ? scaleX : scaleY));
      updates.points = newPoints;
    } else if (type === "triangle") {
      const newPoints = [0, updates.height, updates.width / 2, 0, updates.width, updates.height];
      updates.points = newPoints;
    }
    updateElement(id, updates);
    node.scaleX(1);
    node.scaleY(1);
  };

  // Handle image upload
  const handleImageUpload = (e, id) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        updateElement(id, { imageUrl: ev.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle drop for drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    stageRef.current.setPointersPositions(e);
    const position = stageRef.current.getPointerPosition();
    const url = e.dataTransfer.getData("URL");
    if (url) {
      const newId = Date.now();
      addElement("image");
      updateElement(newId, { imageUrl: url, x: position.x, y: position.y });
    }
  };

  const handlePageSizeChange = (e) => {
    const size = pageSizes[e.target.value];
    if (size) {
      setPageWidth(size.width);
      setPageHeight(size.height);
    }
  };

  // Handle element click for selection
  const handleElementClick = (e, id) => {
    e.cancelBubble = true;
    if (e.evt.shiftKey) {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
      );
    } else {
      setSelectedIds([id]);
    }
  };

  // Handle stage mouse down for selection rectangle
  const handleStageMouseDown = (e) => {
    if (e.target !== stageRef.current) return;
    const pos = stageRef.current.getPointerPosition();
    setSelectStart(pos);
    setSelectRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
    setSelecting(true);
  };

  const handleStageMouseMove = (e) => {
    if (!selecting) return;
    const pos = stageRef.current.getPointerPosition();
    setSelectRect({
      x: Math.min(selectStart.x, pos.x),
      y: Math.min(selectStart.y, pos.y),
      width: Math.abs(pos.x - selectStart.x),
      height: Math.abs(pos.y - selectStart.y),
    });
  };

  const handleStageMouseUp = (e) => {
    if (!selecting) return;
    setSelecting(false);
    const ids = elements
      .filter((el) => {
        const node = stageRef.current.findOne(`#${el.id}`);
        if (node) {
          const nodeRect = node.getClientRect({ relativeTo: stageRef.current });
          return Konva.Util.haveIntersection(selectRect, nodeRect);
        }
        return false;
      })
      .map((el) => el.id);
    if (ids.length > 0) {
      if (e.evt.shiftKey) {
        setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
      } else {
        setSelectedIds(ids);
      }
    }
    setSelectRect(null);
  };

  // Handle wheel for zoom
  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    let direction = e.evt.deltaY > 0 ? -1 : 1;
    if (e.evt.ctrlKey) direction = -direction;
    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    stage.scale({ x: newScale, y: newScale });
    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    stage.position(newPos);
    stage.batchDraw();
  };

  const selectedEls = elements.filter((el) => selectedIds.includes(el.id));
  const selectedEl = selectedIds.length === 1 ? elements.find((el) => el.id === selectedIds[0]) : null;

  // Alignment functions
  const alignLeft = () => {
    const minX = Math.min(...selectedEls.map((el) => el.x));
    selectedEls.forEach((el) => updateElement(el.id, { x: minX }));
  };

  const alignRight = () => {
    const maxX = Math.max(...selectedEls.map((el) => el.x + el.width));
    selectedEls.forEach((el) => updateElement(el.id, { x: maxX - el.width }));
  };

  const alignTop = () => {
    const minY = Math.min(...selectedEls.map((el) => el.y));
    selectedEls.forEach((el) => updateElement(el.id, { y: minY }));
  };

  const alignBottom = () => {
    const maxY = Math.max(...selectedEls.map((el) => el.y + el.height));
    selectedEls.forEach((el) => updateElement(el.id, { y: maxY - el.height }));
  };

  const alignCenterHorizontal = () => {
    const centers = selectedEls.map((el) => el.x + el.width / 2);
    const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
    selectedEls.forEach((el) => updateElement(el.id, { x: avgCenter - el.width / 2 }));
  };

  const alignCenterVertical = () => {
    const centers = selectedEls.map((el) => el.y + el.height / 2);
    const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
    selectedEls.forEach((el) => updateElement(el.id, { y: avgCenter - el.height / 2 }));
  };

  // Distribute functions
  const distributeHorizontal = () => {
    if (selectedEls.length < 3) return;
    const sorted = [...selectedEls].sort((a, b) => a.x - b.x);
    const minX = sorted[0].x;
    const maxX = sorted[sorted.length - 1].x + sorted[sorted.length - 1].width;
    const totalWidth = sorted.reduce((sum, el) => sum + el.width, 0);
    const totalSpace = maxX - minX - totalWidth;
    const space = totalSpace / (sorted.length - 1);
    let currentX = minX;
    sorted.forEach((el, i) => {
      if (i > 0) {
        currentX += sorted[i - 1].width + space;
      }
      updateElement(el.id, { x: currentX });
    });
  };

  const distributeVertical = () => {
    if (selectedEls.length < 3) return;
    const sorted = [...selectedEls].sort((a, b) => a.y - b.y);
    const minY = sorted[0].y;
    const maxY = sorted[sorted.length - 1].y + sorted[sorted.length - 1].height;
    const totalHeight = sorted.reduce((sum, el) => sum + el.height, 0);
    const totalSpace = maxY - minY - totalHeight;
    const space = totalSpace / (sorted.length - 1);
    let currentY = minY;
    sorted.forEach((el, i) => {
      if (i > 0) {
        currentY += sorted[i - 1].height + space;
      }
      updateElement(el.id, { y: currentY });
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex bg-black/50">
      <div className="bg-white w-full h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-[#2b6777] to-[#34495e] text-white rounded-t-lg">
          <h2 className="text-lg font-bold">PDF Designer - {entity}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors">
            <FaTimes />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Toolbar */}
          <div className="w-64 border-r p-4 space-y-4 bg-gray-50 overflow-y-auto shadow-lg">
            <h3 className="font-semibold text-[#2b6777] text-lg">Page Settings</h3>
            <div className="mb-2">
              <label className="block text-sm font-semibold text-gray-700">Page Layout</label>
              <select
                className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                onChange={handlePageSizeChange}
              >
                <option value="">Select Layout</option>
                {Object.keys(pageSizes).map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-2">
              <label className="block text-sm font-semibold text-gray-700">Page Width (pt)</label>
              <input
                type="number"
                className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                value={pageWidth}
                onChange={(e) => setPageWidth(Number(e.target.value))}
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-semibold text-gray-700">Page Height (pt)</label>
              <input
                type="number"
                className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                value={pageHeight}
                onChange={(e) => setPageHeight(Number(e.target.value))}
              />
            </div>

            <h3 className="font-semibold text-[#2b6777] text-lg">Add Elements</h3>
            <button
              className="w-full py-2 px-3 bg-[#2b6777] text-white rounded-lg hover:bg-[#34495e] transition-colors shadow-md"
              onClick={() => addElement("text")}
            >
              <FaFont className="inline mr-2" /> Add Text
            </button>
            <button
              className="w-full py-2 px-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
              onClick={() => addElement("table")}
            >
              <FaTable className="inline mr-2" /> Add Table
            </button>
            <button
              className="w-full py-2 px-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors shadow-md"
              onClick={() => addElement("image")}
            >
              <FaImage className="inline mr-2" /> Add Image
            </button>
            <button
              className="w-full py-2 px-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
              onClick={addShape}
            >
              <FaShapes className="inline mr-2" /> Add Shape
            </button>

            <div className="mt-6">
              <h3 className="font-semibold text-[#2b6777] text-lg">Available Fields</h3>
              {availableFields &&
                Object.keys(availableFields).map((section) => (
                  <div key={section} className="mb-2">
                    <h4 className="font-semibold text-gray-800">{section}</h4>
                    {availableFields[section].map((f) => (
                      <div
                        key={f}
                        className="p-2 bg-gray-100 rounded-lg my-1 cursor-pointer hover:bg-gray-200 transition-colors text-sm shadow-sm"
                        onClick={() => addElement("text", f)}
                      >
                        {f} → {`{data.${f}}`}
                      </div>
                    ))}
                  </div>
                ))}
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-[#2b6777] text-lg">Reusable Components</h3>
              {components.map((comp) => (
                <div
                  key={comp.componentId}
                  className="p-2 bg-gray-100 rounded-lg my-1 cursor-pointer hover:bg-gray-200 transition-colors text-sm shadow-sm"
                  onClick={() => addComponent(comp)}
                >
                  {comp.type} - {comp.text || comp.componentId}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-[#2b6777] text-lg">Saved Designs</h3>
              {savedDesigns.map((design) => (
                <div
                  key={design.id}
                  className="p-2 bg-gray-100 rounded-lg my-1 cursor-pointer hover:bg-gray-200 transition-colors text-sm shadow-sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("token");
                      const response = await axios.get(`${API_URL}/designs/${design.entity}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const { elements, layers, pageWidth, pageHeight } = response.data;
                      setElements(elements);
                      setLayers(layers);
                      setPageWidth(pageWidth);
                      setPageHeight(pageHeight);
                      yElements.delete(0, yElements.length);
                      yElements.push(elements);
                      alert(`Design ${design.entity} loaded successfully`);
                    } catch (error) {
                      console.error("Error loading design:", error);
                      alert("Error loading design");
                    }
                  }}
                >
                  {design.entity}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-[#2b6777] text-lg">Export/Import Design</h3>
              <button
                className="w-full py-2 px-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors shadow-md"
                onClick={exportDesign}
              >
                <FaFileExport className="inline mr-2" /> Export Design
              </button>
              <div className="mt-2">
                <label className="block text-sm font-semibold text-gray-700">Import Design</label>
                <input
                  type="file"
                  accept=".json"
                  className="w-full p-2 border rounded-lg bg-white shadow-sm"
                  onChange={importDesign}
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-[#2b6777] text-lg">Layers</h3>
              <button
                className="w-full py-2 px-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors shadow-md"
                onClick={addLayer}
              >
                <FaLayerGroup className="inline mr-2" /> Add Layer
              </button>
              {layers.map((layer) => (
                <div key={layer.id} className="flex items-center p-2 bg-gray-100 rounded-lg my-1 shadow-sm">
                  <input
                    type="checkbox"
                    checked={layer.visible}
                    onChange={() => toggleLayer(layer.id, "visible")}
                    className="mr-2 accent-[#2b6777]"
                  />
                  <button
                    onClick={() => toggleLayer(layer.id, "locked")}
                    className={`mr-2 ${layer.locked ? "text-red-500" : "text-gray-500"} hover:text-[#2b6777] transition-colors`}
                  >
                    <FaLock />
                  </button>
                  <span className="text-gray-800">{layer.name}</span>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-[#2b6777] text-lg">Audit Trail</h3>
              <div className="max-h-40 overflow-y-auto bg-gray-100 p-2 rounded-lg shadow-inner">
                {auditTrail.map((entry, i) => (
                  <div key={i} className="text-sm text-gray-600 mb-1">
                    {entry.action} by {entry.user} at {new Date(entry.timestamp).toLocaleTimeString()}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div
            ref={containerRef}
            className="flex-1 relative bg-gray-100 overflow-hidden flex justify-center items-center"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <Stage
              ref={stageRef}
              draggable
              onWheel={handleWheel}
              onMouseDown={handleStageMouseDown}
              onMouseMove={handleStageMouseMove}
              onMouseUp={handleStageMouseUp}
            >
              <Layer>
                <Rect x={0} y={0} width={pageWidth} height={pageHeight} fill="white" />
              </Layer>
              {gridVisible && <Layer>{renderGrid()}</Layer>}
              {layers.map(
                (layer) =>
                  layer.visible && (
                    <Layer key={layer.id}>
                      {elements
                        .filter((el) => el.layer === layer.id && (!layer.locked || el.isConditional))
                        .map((el) => {
                          const commonProps = {
                            id: `${el.id}`,
                            x: el.x,
                            y: el.y,
                            rotation: el.rotation,
                            opacity: el.opacity,
                            draggable: !layer.locked,
                            onClick: (e) => handleElementClick(e, el.id),
                            onDragEnd: (e) => handleDragEnd(e, el.id),
                            onTransformEnd: (e) => handleTransformEnd(e, el.id, el.type === "shape" ? el.shapeType : el.type),
                            shadowColor: el.shadowColor,
                            shadowBlur: el.shadowBlur,
                          };
                          if (el.type === "text") {
                            return (
                              <Group key={el.id} {...commonProps}>
                                <Rect
                                  width={el.width}
                                  height={el.height}
                                  fill={el.bgColor}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                />
                                <Text
                                  text={el.text.replace(/{data\.([^}]+)}/g, (_, key) => sampleData[key] || sampleData.Summary?.[key] || sampleData.Items?.[0]?.[key] || "")}
                                  fontSize={el.fontSize}
                                  fontFamily={el.fontFamily}
                                  fill={el.fill}
                                  align={el.textAlign}
                                  fontStyle={el.fontStyle}
                                  width={el.width}
                                  height={el.height}
                                  wrap="word"
                                  verticalAlign="top"
                                />
                              </Group>
                            );
                          }
                          if (el.type === "table") {
                            let xOffset = 0;
                            return (
                              <Group key={el.id} {...commonProps} clip={{ x: 0, y: 0, width: el.width, height: el.height }}>
                                <Rect
                                  width={el.width}
                                  height={el.height}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                  fill={el.bgColor}
                                />
                                {/* Table Header */}
                                {el.dataFields.map((field, colIndex) => {
                                  const colWidth = el.columnWidths[colIndex] || el.width / el.dataFields.length;
                                  const header = (
                                    <Group key={`header-${field}`}>
                                      <Rect
                                        x={xOffset}
                                        y={0}
                                        width={colWidth}
                                        height={el.headerHeight}
                                        fill={el.headerBgColor}
                                        stroke={el.gridBorderColor}
                                        strokeWidth={el.gridBorderWidth}
                                      />
                                      <Text
                                        x={xOffset + el.cellPadding}
                                        y={(el.headerHeight - el.headerFontSize) / 2}
                                        width={colWidth - el.cellPadding * 2}
                                        text={field}
                                        fontSize={el.headerFontSize}
                                        fontFamily={el.fontFamily}
                                        fontStyle="bold"
                                        fill={el.headerFill}
                                        align={el.headerTextAlign}
                                        wrap="char"
                                        verticalAlign="middle"
                                      />
                                    </Group>
                                  );
                                  xOffset += colWidth;
                                  return header;
                                })}
                                {/* Table Rows */}
                                {(sampleData.Items || []).map((item, rowIndex) => {
                                  xOffset = 0;
                                  return el.dataFields.map((field, colIndex) => {
                                    const colWidth = el.columnWidths[colIndex] || el.width / el.dataFields.length;
                                    const cell = (
                                      <Group key={`${rowIndex}-${field}`}>
                                        <Rect
                                          x={xOffset}
                                          y={el.headerHeight + rowIndex * el.rowHeight}
                                          width={colWidth}
                                          height={el.rowHeight}
                                          stroke={el.gridBorderColor}
                                          strokeWidth={el.gridBorderWidth}
                                        />
                                        <Text
                                          x={xOffset + el.cellPadding}
                                          y={el.headerHeight + rowIndex * el.rowHeight + (el.rowHeight - el.bodyFontSize) / 2}
                                          width={colWidth - el.cellPadding * 2}
                                          text={`${item[field] || "N/A"}`}
                                          fontSize={el.bodyFontSize}
                                          fontFamily={el.fontFamily}
                                          fill={el.bodyFill}
                                          align={el.bodyTextAlign}
                                          wrap="char"
                                          verticalAlign="middle"
                                        />
                                      </Group>
                                    );
                                    xOffset += colWidth;
                                    return cell;
                                  });
                                })}
                              </Group>
                            );
                          }
                          if (el.type === "image") {
                            return (
                              <KonvaImageComponent
                                key={el.id}
                                src={el.imageUrl || "https://via.placeholder.com/150"}
                                {...commonProps}
                                width={el.width}
                                height={el.height}
                              />
                            );
                          }
                          if (el.type === "shape") {
                            if (el.shapeType === "rectangle") {
                              return (
                                <Rect
                                  key={el.id}
                                  {...commonProps}
                                  width={el.width}
                                  height={el.height}
                                  fill={el.bgColor}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                />
                              );
                            }
                            if (el.shapeType === "circle") {
                              return (
                                <Circle
                                  key={el.id}
                                  {...commonProps}
                                  radius={Math.min(el.width, el.height) / 2}
                                  fill={el.bgColor}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                />
                              );
                            }
                            if (el.shapeType === "arrow") {
                              return (
                                <Arrow
                                  key={el.id}
                                  {...commonProps}
                                  points={el.points || [0, 0, el.width, 0]}
                                  pointerLength={10}
                                  pointerWidth={10}
                                  fill={el.bgColor}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                />
                              );
                            }
                            if (el.shapeType === "triangle") {
                              return (
                                <Line
                                  key={el.id}
                                  {...commonProps}
                                  points={el.points || [0, el.height, el.width / 2, 0, el.width, el.height]}
                                  closed={true}
                                  fill={el.bgColor}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                />
                              );
                            }
                            if (el.shapeType === "line") {
                              return (
                                <Line
                                  key={el.id}
                                  {...commonProps}
                                  points={el.points || [0, 0, el.width, 0]}
                                  stroke={el.borderColor}
                                  strokeWidth={el.borderWidth}
                                />
                              );
                            }
                          }
                          return null;
                        })}
                    </Layer>
                  )
              )}
              <Layer>
                {selectRect && (
                  <Rect
                    {...selectRect}
                    fill="blue"
                    opacity={0.2}
                    stroke="blue"
                    strokeWidth={1}
                  />
                )}
                <Transformer
                  ref={transformerRef}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>
            <button
              className="absolute top-2 right-2 bg-gray-200 p-2 rounded-lg hover:bg-gray-300 transition-colors shadow-md"
              onClick={() => setGridVisible(!gridVisible)}
            >
              {gridVisible ? "Hide Grid" : "Show Grid"}
            </button>
          </div>

          {/* Properties Sidebar */}
          {selectedIds.length > 0 && (
            <div className="w-64 border-l p-4 bg-gray-50 overflow-y-auto relative shadow-lg">
              <button
                onClick={() => setSelectedIds([])}
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <FaTimes />
              </button>
              {selectedIds.length === 1 ? (
                <>
                  <h3 className="font-semibold mb-2 text-[#2b6777] text-lg">Properties</h3>
                  <div className="mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Layer</label>
                    <select
                      className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                      value={selectedEl.layer}
                      onChange={(e) => updateElement(selectedEl.id, { layer: e.target.value })}
                    >
                      {layers.map((layer) => (
                        <option key={layer.id} value={layer.id}>
                          {layer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedEl.type === "text" && (
                    <>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Text</label>
                        <input
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.text}
                          onChange={(e) => updateElement(selectedEl.id, { text: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Font Size</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.fontSize}
                          onChange={(e) => updateElement(selectedEl.id, { fontSize: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Font Family</label>
                        <select
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.fontFamily}
                          onChange={(e) => updateElement(selectedEl.id, { fontFamily: e.target.value })}
                        >
                          <option value="Arial">Arial</option>
                          <option value="Helvetica">Helvetica</option>
                          <option value="Times New Roman">Times New Roman</option>
                          <option value="Courier">Courier</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Font Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.fill}
                          onChange={(e) => updateElement(selectedEl.id, { fill: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Text Align</label>
                        <select
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.textAlign}
                          onChange={(e) => updateElement(selectedEl.id, { textAlign: e.target.value })}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Font Style</label>
                        <select
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.fontStyle}
                          onChange={(e) => updateElement(selectedEl.id, { fontStyle: e.target.value })}
                        >
                          <option value="normal">Normal</option>
                          <option value="bold">Bold</option>
                          <option value="italic">Italic</option>
                          <option value="bold italic">Bold Italic</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Background Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.bgColor || "transparent"}
                          onChange={(e) => updateElement(selectedEl.id, { bgColor: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Border Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.borderColor}
                          onChange={(e) => updateElement(selectedEl.id, { borderColor: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Border Width</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.borderWidth}
                          onChange={(e) => updateElement(selectedEl.id, { borderWidth: Number(e.target.value) })}
                        />
                      </div>
                    </>
                  )}
                  {(selectedEl.type === "table" || selectedEl.type === "image" || selectedEl.type === "shape") && (
                    <>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Width</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.width}
                          onChange={(e) => updateElement(selectedEl.id, { width: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Height</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.height}
                          onChange={(e) => updateElement(selectedEl.id, { height: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Border Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.borderColor}
                          onChange={(e) => updateElement(selectedEl.id, { borderColor: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Background Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.bgColor || "transparent"}
                          onChange={(e) => updateElement(selectedEl.id, { bgColor: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Border Width</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.borderWidth}
                          onChange={(e) => updateElement(selectedEl.id, { borderWidth: Number(e.target.value) })}
                        />
                      </div>
                    </>
                  )}
                  {selectedEl.type === "table" && (
                    <>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Data Fields</label>
                        <select
                          multiple
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.dataFields}
                          onChange={(e) => {
                            const fields = Array.from(e.target.selectedOptions, (option) => option.value);
                            const { columnWidths, width } = calculateTableDimensions(fields, sampleData.Items || [], selectedEl.headerFontSize, selectedEl.bodyFontSize, selectedEl.cellPadding, selectedEl.headerHeight, selectedEl.rowHeight);
                            updateElement(selectedEl.id, { dataFields: fields, columnWidths, width });
                          }}
                        >
                          {(availableFields.Items || []).map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Header Font Size</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.headerFontSize}
                          onChange={(e) => updateElement(selectedEl.id, { headerFontSize: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Header Font Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.headerFill}
                          onChange={(e) => updateElement(selectedEl.id, { headerFill: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Header Background Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.headerBgColor}
                          onChange={(e) => updateElement(selectedEl.id, { headerBgColor: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Header Text Align</label>
                        <select
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.headerTextAlign}
                          onChange={(e) => updateElement(selectedEl.id, { headerTextAlign: e.target.value })}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Body Font Size</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.bodyFontSize}
                          onChange={(e) => updateElement(selectedEl.id, { bodyFontSize: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Body Font Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.bodyFill}
                          onChange={(e) => updateElement(selectedEl.id, { bodyFill: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Body Text Align</label>
                        <select
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.bodyTextAlign}
                          onChange={(e) => updateElement(selectedEl.id, { bodyTextAlign: e.target.value })}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Cell Padding</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.cellPadding}
                          onChange={(e) => updateElement(selectedEl.id, { cellPadding: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Grid Border Color</label>
                        <input
                          type="color"
                          className="w-full p-1 border rounded-lg bg-white shadow-sm"
                          value={selectedEl.gridBorderColor}
                          onChange={(e) => updateElement(selectedEl.id, { gridBorderColor: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Grid Border Width</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.gridBorderWidth}
                          onChange={(e) => updateElement(selectedEl.id, { gridBorderWidth: Number(e.target.value) })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Font Family</label>
                        <select
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.fontFamily}
                          onChange={(e) => updateElement(selectedEl.id, { fontFamily: e.target.value })}
                        >
                          <option value="Arial">Arial</option>
                          <option value="Helvetica">Helvetica</option>
                          <option value="Times New Roman">Times New Roman</option>
                          <option value="Courier">Courier</option>
                        </select>
                      </div>
                    </>
                  )}
                  {selectedEl.type === "image" && (
                    <>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Image URL</label>
                        <input
                          className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                          value={selectedEl.imageUrl || ""}
                          onChange={(e) => updateElement(selectedEl.id, { imageUrl: e.target.value })}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Upload Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          className="w-full p-2 border rounded-lg bg-white shadow-sm"
                          onChange={(e) => handleImageUpload(e, selectedEl.id)}
                        />
                      </div>
                    </>
                  )}
                  {selectedEl.type === "shape" && (
                    <div className="mb-2">
                      <label className="block text-sm font-semibold text-gray-700">Shape Type</label>
                      <select
                        className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                        value={selectedEl.shapeType}
                        onChange={(e) => updateElement(selectedEl.id, { shapeType: e.target.value })}
                      >
                        <option value="rectangle">Rectangle</option>
                        <option value="circle">Circle</option>
                        <option value="arrow">Arrow</option>
                        <option value="triangle">Triangle</option>
                        <option value="line">Line</option>
                      </select>
                    </div>
                  )}
                  <div className="mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Rotation</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                      value={selectedEl.rotation}
                      onChange={(e) => updateElement(selectedEl.id, { rotation: Number(e.target.value) })}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Opacity</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      className="w-full accent-[#2b6777]"
                      value={selectedEl.opacity}
                      onChange={(e) => updateElement(selectedEl.id, { opacity: Number(e.target.value) })}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Shadow Color</label>
                    <input
                      type="color"
                      className="w-full p-1 border rounded-lg bg-white shadow-sm"
                      value={selectedEl.shadowColor}
                      onChange={(e) => updateElement(selectedEl.id, { shadowColor: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Shadow Blur</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2b6777]"
                      value={selectedEl.shadowBlur}
                      onChange={(e) => updateElement(selectedEl.id, { shadowBlur: Number(e.target.value) })}
                    />
                  </div>
                  <button
                    className="flex items-center gap-2 px-4 py-2 mt-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                    onClick={saveComponent}
                  >
                    <FaSave /> Save as Component
                  </button>
                  <button
                    className="flex items-center gap-2 px-4 py-2 mt-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-md"
                    onClick={() => removeElement(selectedEl.id)}
                  >
                    <FaTrash /> Remove
                  </button>
                </>
              ) : (
                <>
                  <h3 className="font-semibold mb-2 text-[#2b6777] text-lg">{selectedIds.length} Items Selected</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                      onClick={alignLeft}
                    >
                      Align Left
                    </button>
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                      onClick={alignRight}
                    >
                      Align Right
                    </button>
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                      onClick={alignTop}
                    >
                      Align Top
                    </button>
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                      onClick={alignBottom}
                    >
                      Align Bottom
                    </button>
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                      onClick={alignCenterHorizontal}
                    >
                      Center H
                    </button>
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                      onClick={alignCenterVertical}
                    >
                      Center V
                    </button>
                    <button
                      className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
                      onClick={distributeHorizontal}
                    >
                      Distribute H
                    </button>
                    <button
                      className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
                      onClick={distributeVertical}
                    >
                      Distribute V
                    </button>
                  </div>
                  <button
                    className="flex items-center gap-2 px-4 py-2 mt-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-md"
                    onClick={() => selectedIds.forEach((id) => removeElement(id))}
                  >
                    <FaTrash /> Remove All
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gradient-to-r from-[#2b6777] to-[#34495e] flex justify-end gap-3 rounded-b-lg">
          <button
            className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-md ${
              isSaving ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
            }`}
            onClick={saveDesign}
            disabled={isSaving}
          >
            <FaSave /> {isSaving ? "Saving..." : "Save Design"}
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#2b6777] rounded-lg hover:bg-gray-100 transition-colors shadow-md"
            onClick={exportPDF}
          >
            <FaDownload /> Export PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFDesigner;